import React, { useState } from 'react';
import { Tab, Tabs, Typography, styled } from '@mui/material';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import InteractiveTable from 'components/InteractiveTable';
import Heading from 'components/Heading';
import { AllocationRule } from 'further-types/lp/allocations';
import { sortAllocations } from '../utils/sortAllocations';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(8),
}));

interface GeneralCapitalCallCardProps {
  generalCapitalAction: {
    actionId: string;
    entryId: string;
    name: string;
    ledgerDate: CalendarDay;
    actionDueDate?: CalendarDay;
    totalAmountLcy: number;
    allocationRule: AllocationRule;
    originalInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
    }>;
    newInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
      excludeFromAllocation: boolean;
      callOverpaidOrUnderpaid: number;
      isNewTransferee?: boolean;
    }>;
    numberOfEntries: number;
  };
  index: number;
  isLastElement: boolean;
  baseCurrency: string;
  transferDate: CalendarDay;
}

const GeneralCapitalCallCard: React.FC<GeneralCapitalCallCardProps> = ({
  generalCapitalAction,
  index,
  isLastElement,
  baseCurrency,
  transferDate,
}) => {
  const [activeTab, setActiveTab] = useState(1);

  const sortedOriginalBreakdown = sortAllocations(
    generalCapitalAction.originalInvestorBreakdown,
  );
  const sortedNewBreakdown = sortAllocations(
    generalCapitalAction.newInvestorBreakdown,
  );

  const rows =
    activeTab === 0
      ? sortedOriginalBreakdown.map((row) => ({
          ...row,
          callOverpaidOrUnderpaid: 0,
        }))
      : sortedNewBreakdown;

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const columns = [
    {
      field: 'investorName',
      headerName: 'Investor',
      renderCell: ({ row }) => row.investorName,
      flex: 1,
    },
    {
      field: 'totalCommitmentLcy',
      headerName: 'Total Commitment',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'calledFundsLcy',
      headerName: 'Called funds (LCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.calledFundsLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'allocationPercentage',
      headerName: 'Allocation % of called funds',
      renderCell: ({ row }) =>
        numberToPercentageString(row.allocationPercentage, {
          accuracy: 2,
          noMultiply: true,
        }),
      flex: 1,
    },
    ...(activeTab === 1
      ? [
          {
            field: 'callOverpaidOrUnderpaid',
            headerName: 'Call underpaid/(overpaid)',
            renderCell: ({ row }) =>
              numberToCurrencyString(row.callOverpaidOrUnderpaid, {
                fallback: '-',
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
            flex: 1,
          },
        ]
      : []),
  ];

  return (
    <EqualisationsConcertina
      key={index}
      gridTemplateColumns="60% 20% 18% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            <Tab
              label={
                <Heading variant="h5">
                  Allocation before {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
            <Tab
              label={
                <Heading variant="h5">
                  Allocation after {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
          </StyledTabs>
          <InteractiveTable
            id="general-capital-call-breakdown-table"
            columns={columns}
            rows={rows}
            scrollable={true}
          />
        </>
      }
      concertinaItems={[
        <Typography variant="body1" key="name">
          {generalCapitalAction?.name ?? ''}
        </Typography>,
        <Typography variant="body1" key="ledger-date">
          {generalCapitalAction?.ledgerDate
            ? generalCapitalAction.ledgerDate.toLocalDateLabel()
            : '-'}
        </Typography>,
        <Typography variant="body1" key="total-amount">
          {numberToCurrencyString(generalCapitalAction.totalAmountLcy, {
            fallback: '-',
            currency: baseCurrency,
          })}
        </Typography>,
      ]}
    />
  );
};

export default GeneralCapitalCallCard;
